import { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, Upload, Loader2, X, Save, ArrowRight, FileText, Plus, ImagePlus, AlertTriangle, Trash2,
} from 'lucide-react';
import SaveDraftModal from '../shared/SaveDraftModal';
import ReceiptViewerModal from '../shared/ReceiptViewerModal';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, fieldStyle, selectStyle, navyBtn } from '../../styles/tokens';
import { useAuth, userIsAdmin, userIsEngineer, userIsReviewer } from '../../context/AuthContext';
import { useOptions } from '../../context/OptionsContext';
import { PAYMENT_METHODS, PR_TAX_RATES } from '@shared/constants';
import { createPreviewUrl, revokePreviewUrl } from '../../utils/fileUtils';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';

function genId() {
  return Math.random().toString(36).slice(2, 10);
}

function fmt(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function blankLineItem() {
  return {
    id: genId(),
    description: '',
    amount: '',
    quantity: 1,
    unit: '',
    unitPrice: '',
    taxRate: '11.5',
    supervisorId: '',
    projectId: '',
    aiSuggested: false,
    aiFields: {},
    isPersonal: false,
    expenseType: 'project',
  };
}

function parseReceiptTotal(raw) {
  if (raw == null) return null;
  const num = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
  return isNaN(num) ? null : num;
}

export default function UploadPage({
  initialItems, editReceipt,
  onSubmitted, onSavedDraft, onDiscarded, onResubmitted, onBack,
}) {
  const { supervisors, projects } = useOptions();
  const { user } = useAuth();
  const canEditTax = userIsReviewer(user) || userIsAdmin(user);
  const isEmployeeOnly = !userIsAdmin(user) && !userIsEngineer(user) && !userIsReviewer(user);
  const isEditMode = !!editReceipt;
  const isDraftEdit = isEditMode && editReceipt?.status === 'draft';
  const isPartialReturn = isEditMode && editReceipt?.status === 'partially_returned';

  // Receipt-level state
  const [receipt, setReceipt] = useState(() => {
    if (isEditMode) {
      return {
        receiptId: editReceipt.id,
        file: null, newFile: null,
        fileName: editReceipt.fileName || '',
        fileType: editReceipt.fileType || '',
        previewUrl: editReceipt.previewUrl || null,
        localPreview: null,
        vendor: editReceipt.vendor || '',
        date: editReceipt.date || '',
        paymentMethod: editReceipt.paymentMethod || '',
        receiptTotal: editReceipt.receiptTotal != null ? String(editReceipt.receiptTotal) : (editReceipt.amount != null ? String(editReceipt.amount) : ''),
        subtotal: editReceipt.subtotal != null ? String(editReceipt.subtotal) : '',
        invoiceNumber: editReceipt.invoiceNumber || '',
        cardType: editReceipt.cardType || '',
        aiSuggested: editReceipt.aiSuggested || false,
        aiConfidence: editReceipt.aiConfidence || null,
        analyzing: false,
      };
    }
    return {
      file: null, fileName: '', fileType: '',
      previewUrl: null, localPreview: null,
      vendor: '', date: '', paymentMethod: '', cardType: '',
      receiptTotal: '', subtotal: '',
      invoiceNumber: '',
      aiSuggested: false, aiConfidence: null, analyzing: false,
    };
  });

  // For partially_returned receipts, separate approved items (read-only) from sent_back items (editable)
  const [approvedItems] = useState(() => {
    if (isPartialReturn && editReceipt.items?.length > 0) {
      return editReceipt.items.filter(it => it.status !== 'sent_back');
    }
    return [];
  });

  // Line items state — always start with at least one blank item
  const [lineItems, setLineItems] = useState(() => {
    if (isEditMode && editReceipt.items?.length > 0) {
      const editableItems = isPartialReturn
        ? editReceipt.items.filter(it => it.status === 'sent_back')
        : editReceipt.items;
      return editableItems.map(it => {
        const qty = parseInt(it.quantity) || 1;
        const amt = it.amount !== null ? parseFloat(it.amount) : 0;
        return {
          id: genId(),
          description: it.description || '',
          amount: it.amount !== null ? String(it.amount) : '',
          quantity: qty,
          unit: it.unit || '',
          unitPrice: qty > 0 ? String((amt / qty).toFixed(2)) : '',
          taxRate: it.taxRate != null && it.taxRate !== 0 ? String(it.taxRate) : '11.5',
          supervisorId: it.supervisorId ? String(it.supervisorId) : '',
          projectId: it.projectId ? String(it.projectId) : '',
          aiSuggested: it.aiSuggested || false,
          aiFields: it.aiFields || {},
          isPersonal: it.isPersonal || false,
          expenseType: it.expenseType || 'project',
        };
      });
    }
    if (initialItems?.length > 0) {
      return initialItems.map(it => ({ id: genId(), ...it }));
    }
    return [blankLineItem()];
  });

  const [applySameProject, setApplySameProject] = useState(false);
  const [globalProjectId, setGlobalProjectId] = useState('');
  const [applySameType, setApplySameType] = useState(false);
  const [globalExpenseType, setGlobalExpenseType] = useState('project');
  const [applySameTax, setApplySameTax] = useState(true);
  const [globalTaxRate, setGlobalTaxRate] = useState('11.5');
  const [validationErrors, setValidationErrors] = useState({});
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const replaceFileRef = useRef(null);

  useEffect(() => {
    return () => { if (receipt.localPreview) revokePreviewUrl(receipt.localPreview); };
  }, []);

  const hasFile = !!receipt.file || !!receipt.previewUrl;
  const hasItems = lineItems.some(i => i.amount || i.description?.trim());
  const hasContent = hasFile || receipt.vendor?.trim() || hasItems;
  const subtotal = lineItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalWithTax = lineItems.reduce((s, i) => {
    const amt = parseFloat(i.amount) || 0;
    const tax = amt * (parseFloat(i.taxRate) || 0) / 100;
    return s + amt + tax;
  }, 0);
  // Employee sees subtotal; everyone else sees total with tax
  // Only reviewer/admin sees total with tax; everyone else sees subtotal
  const total = canEditTax ? totalWithTax : subtotal;

  // ── File upload + AI analysis ──
  async function handleFileUpload(file) {
    const localPreview = createPreviewUrl(file);
    setReceipt(prev => ({
      ...prev, file, fileName: file.name, fileType: file.type,
      localPreview, analyzing: true,
    }));

    const result = await analyzeFile(file);
    let parsedTotal = parseReceiptTotal(result?.receiptTotal);
    // Fallback: compute total from items if AI didn't return a valid receiptTotal
    if (parsedTotal == null && result?.items?.length > 0) {
      parsedTotal = Math.round(result.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) * 100) / 100;
    }
    let parsedSubtotal = parseReceiptTotal(result?.subtotal);
    // Fallback: compute subtotal from item amounts if AI didn't extract it
    if (parsedSubtotal == null && result?.items?.length > 0) {
      parsedSubtotal = Math.round(result.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) * 100) / 100;
    }
    setReceipt(prev => ({
      ...prev, analyzing: false,
      aiSuggested: !!result, aiConfidence: result?.confidence || null,
      receiptTotal: parsedTotal != null ? String(parsedTotal) : prev.receiptTotal,
      subtotal: parsedSubtotal != null ? String(parsedSubtotal) : prev.subtotal,
      vendor: prev.vendor || result?.vendor || '',
      date: prev.date || result?.date || '',
      invoiceNumber: prev.invoiceNumber || result?.invoiceNumber || '',
    }));

    if (result?.items?.length > 0) {
      setLineItems(result.items.map((item) => {
        const qty = parseInt(item.quantity) || 1;
        const amt = parseFloat(item.amount) || 0;
        return {
          id: genId(),
          description: item.description || '',
          amount: item.amount !== null ? String(item.amount) : '',
          quantity: qty,
          unit: item.unit || '',
          unitPrice: qty > 0 ? String((amt / qty).toFixed(2)) : '',
          taxRate: canEditTax ? (applySameTax ? globalTaxRate : '11.5') : '0',
          category: item.suggestedCategory || '',
          costCenter: item.suggestedCostCenter || '',
          supervisorId: '',
          projectId: applySameProject && globalProjectId ? globalProjectId : '',
          expenseType: applySameType ? globalExpenseType : 'project',
          aiSuggested: true,
          aiFields: {
            description: !!item.description,
            amount: item.amount !== null,
            category: !!item.suggestedCategory,
            costCenter: !!item.suggestedCostCenter,
          },
        };
      }));
    } else if (lineItems.length === 0) {
      setLineItems([blankLineItem()]);
    }

    // Warn if AI couldn't find invoice number
    if (result && !result.invoiceNumber) {
      console.warn('AI could not extract invoice number from receipt');
    }
  }

  async function analyzeFile(file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await client.post('/api/analyze-receipt', form);
      return res.data;
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'receipt_analyze',
          file_type: file?.type,
          file_size: file?.size,
        },
      });
      return null;
    }
  }

  function handleDrop(e) { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]); }
  function handleFileInput(e) { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }

  async function handleReplaceFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const localPreview = createPreviewUrl(file);
    setReceipt(prev => ({
      ...prev, newFile: file, file, localPreview,
      fileName: file.name, fileType: file.type, analyzing: true,
    }));

    const result = await analyzeFile(file);
    let parsedTotal = parseReceiptTotal(result?.receiptTotal);
    // Fallback: compute total from items if AI didn't return a valid receiptTotal
    if (parsedTotal == null && result?.items?.length > 0) {
      parsedTotal = Math.round(result.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) * 100) / 100;
    }
    let parsedSubtotal2 = parseReceiptTotal(result?.subtotal);
    if (parsedSubtotal2 == null && result?.items?.length > 0) {
      parsedSubtotal2 = Math.round(result.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) * 100) / 100;
    }
    setReceipt(prev => ({
      ...prev, analyzing: false,
      aiSuggested: !!result, aiConfidence: result?.confidence || null,
      receiptTotal: parsedTotal != null ? String(parsedTotal) : prev.receiptTotal,
      subtotal: parsedSubtotal2 != null ? String(parsedSubtotal2) : prev.subtotal,
      vendor: result?.vendor || prev.vendor,
      date: result?.date || prev.date,
      invoiceNumber: result?.invoiceNumber || prev.invoiceNumber,
    }));

    if (result?.items?.length > 0) {
      setLineItems(result.items.map((item) => {
        const qty = parseInt(item.quantity) || 1;
        const amt = parseFloat(item.amount) || 0;
        return {
          id: genId(),
          description: item.description || '',
          amount: item.amount !== null ? String(item.amount) : '',
          quantity: qty,
          unit: item.unit || '',
          unitPrice: qty > 0 ? String((amt / qty).toFixed(2)) : '',
          taxRate: canEditTax ? (applySameTax ? globalTaxRate : '11.5') : '0',
          category: item.suggestedCategory || '',
          costCenter: item.suggestedCostCenter || '',
          supervisorId: '',
          projectId: applySameProject && globalProjectId ? globalProjectId : '',
          expenseType: applySameType ? globalExpenseType : 'project',
          aiSuggested: true,
          aiFields: {
            description: !!item.description,
            amount: item.amount !== null,
            category: !!item.suggestedCategory,
            costCenter: !!item.suggestedCostCenter,
          },
        };
      }));
    }
  }

  // ── Line item management ──
  function addLineItem() {
    setLineItems(prev => {
      const newItem = blankLineItem();
      if (!canEditTax) newItem.taxRate = '0';
      if (applySameProject && globalProjectId) {
        newItem.projectId = globalProjectId;
        const project = projects.find(p => String(p.id) === String(globalProjectId));
        if (project?.supervisorId) {
          newItem.supervisorId = String(project.supervisorId);
        }
      }
      if (applySameType) {
        newItem.expenseType = globalExpenseType;
      }
      return [...prev, newItem];
    });
  }

  function updateLineItem(id, field, value) {
    // Clear validation error for this field
    setValidationErrors(prev => {
      if (!prev[id]) return prev;
      const updated = { ...prev[id] };
      delete updated[field];
      return Object.keys(updated).length > 0 ? { ...prev, [id]: updated } : (() => { const { [id]: _, ...rest } = prev; return rest; })();
    });
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updates = { [field]: value, aiFields: { ...item.aiFields, [field]: false } };
      // Auto-fill supervisor when project is selected
      if (field === 'projectId' && value) {
        const project = projects.find(p => String(p.id) === String(value));
        if (project?.supervisorId) {
          updates.supervisorId = String(project.supervisorId);
        }
      }
      // Recalculate amount/unitPrice when quantity, unitPrice, or amount changes
      if (field === 'unitPrice') {
        const up = parseFloat(value) || 0;
        const qty = parseInt(item.quantity) || 1;
        updates.amount = String((up * qty).toFixed(2));
      } else if (field === 'quantity') {
        const qty = parseInt(value) || 1;
        const up = parseFloat(item.unitPrice) || 0;
        if (up > 0) updates.amount = String((up * qty).toFixed(2));
      } else if (field === 'amount') {
        const amt = parseFloat(value) || 0;
        const qty = parseInt(item.quantity) || 1;
        if (qty > 0) updates.unitPrice = String((amt / qty).toFixed(2));
      }
      return { ...item, ...updates };
    }));
  }

  function removeLineItem(id) {
    setLineItems(prev => {
      const remaining = prev.filter(i => i.id !== id);
      if (remaining.length > 0) return remaining;
      const newItem = blankLineItem();
      if (applySameProject && globalProjectId) {
        newItem.projectId = globalProjectId;
        const project = projects.find(p => String(p.id) === String(globalProjectId));
        if (project?.supervisorId) {
          newItem.supervisorId = String(project.supervisorId);
        }
      }
      return [newItem];
    });
  }

  // ── Apply same project to all ──
  function handleApplySameProjectToggle(checked) {
    setApplySameProject(checked);
    if (checked && globalProjectId) {
      const project = projects.find(p => String(p.id) === String(globalProjectId));
      const supId = project?.supervisorId ? String(project.supervisorId) : '';
      setLineItems(prev => prev.map(item => ({ ...item, projectId: globalProjectId, supervisorId: supId })));
    }
  }

  function handleGlobalProjectChange(value) {
    setGlobalProjectId(value);
    const project = projects.find(p => String(p.id) === String(value));
    const supId = project?.supervisorId ? String(project.supervisorId) : '';
    if (applySameProject) {
      setLineItems(prev => prev.map(item => ({ ...item, projectId: value, supervisorId: supId })));
    }
  }

  // ── Apply same type to all ──
  function handleApplySameTypeToggle(checked) {
    setApplySameType(checked);
    if (checked) {
      setLineItems(prev => prev.map(item => ({ ...item, expenseType: globalExpenseType })));
    }
  }

  function handleGlobalExpenseTypeChange(value) {
    setGlobalExpenseType(value);
    if (applySameType) {
      setLineItems(prev => prev.map(item => ({ ...item, expenseType: value })));
    }
  }

  // ── Apply same tax rate to all ──
  function handleApplySameTaxToggle(checked) {
    setApplySameTax(checked);
    if (checked) {
      setLineItems(prev => prev.map(item => ({ ...item, taxRate: globalTaxRate })));
    }
  }

  function handleGlobalTaxRateChange(value) {
    setGlobalTaxRate(value);
    if (applySameTax) {
      setLineItems(prev => prev.map(item => ({ ...item, taxRate: value })));
    }
  }

  // ── Navigation ──
  function handleBack() {
    if ((!isEditMode || isDraftEdit) && hasContent) setShowDraftModal(true);
    else onBack();
  }

  // ── Duplicate check (date + amount, same employee — soft warning) ──
  async function checkForDuplicate() {
    if (!receipt.date || !receipt.receiptTotal) return true;
    try {
      const params = { date: receipt.date, amount: receipt.receiptTotal };
      if (isEditMode && receipt.receiptId) params.excludeId = receipt.receiptId;
      const res = await client.get('/api/receipts/check-duplicate', { params });
      if (res.data.duplicate) {
        const existing = res.data.existing;
        return window.confirm(
          `Ya existe un recibo similar con la misma fecha y monto:\n\n` +
          `• Proveedor: ${existing.vendor || '—'}\n` +
          `• Monto: $${parseFloat(existing.amount).toFixed(2)}\n` +
          `• Fecha: ${existing.date}\n` +
          `• Estado: ${existing.status}\n\n` +
          `¿Está seguro que este no es un duplicado?`
        );
      }
    } catch (err) {
      console.error('Duplicate check failed:', err);
    }
    return true;
  }

  // ── Validation ──
  function validate() {
    if (!hasFile) {
      alert('Por favor, sube una foto del recibo antes de enviar.');
      return false;
    }
    const missing = [];
    if (!receipt.vendor?.trim()) missing.push('Proveedor');
    if (!receipt.date) missing.push('Fecha');
    if (!receipt.paymentMethod) missing.push('Método de Pago');
    if (missing.length > 0) {
      alert(`Por favor, completa los campos del recibo:\n• ${missing.join('\n• ')}`);
      return false;
    }

    const contentItems = lineItems.filter(i => i.description?.trim() || i.amount);
    if (contentItems.length === 0) {
      alert('Por favor, agrega al menos un item antes de enviar.');
      return false;
    }
    const errors = {};
    for (const item of contentItems) {
      const fieldErrors = {};
      if (!item.projectId) fieldErrors.projectId = true;
      if (!item.description?.trim()) fieldErrors.description = true;
      if (!item.amount || parseFloat(item.amount) === 0 || isNaN(parseFloat(item.amount))) fieldErrors.amount = true;
      if (!item.supervisorId) fieldErrors.supervisorId = true;
      if (Object.keys(fieldErrors).length > 0) errors[item.id] = fieldErrors;
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      alert('Por favor, completa todos los campos de cada item (Proyecto, Descripción, Monto, Supervisor).');
      return false;
    }
    setValidationErrors({});

    // Block submission if line items total doesn't match receipt total
    // Employee validates against subtotal; engineer/reviewer validates against total with tax
    if (!canEditTax) {
      // Non-reviewer: validate subtotal (no tax) against the Subtotal field
      const target = parseFloat(receipt.subtotal);
      if (!target || isNaN(target) || target <= 0) {
        alert('Por favor, ingresa el Subtotal del Recibo antes de enviar.');
        return false;
      }
      if (Math.abs(subtotal - target) > 0.02) {
        alert(
          `El subtotal de los items ($${subtotal.toFixed(2)}) no coincide con el subtotal del recibo ($${target.toFixed(2)}).\n\nVerifica que los montos estén correctos antes de enviar.`
        );
        return false;
      }
    } else {
      // Reviewer/Admin: validate items+tax against the stored receipt total
      const declaredTotal = parseFloat(receipt.receiptTotal);
      if (!receipt.receiptTotal || isNaN(declaredTotal) || declaredTotal <= 0) {
        alert('No se encontró el Total del Recibo. Verifica los datos.');
        return false;
      }
      const approvedTotal = approvedItems.reduce((s, i) => {
        const a = parseFloat(i.amount) || 0;
        return s + a + a * (parseFloat(i.taxRate) || 0) / 100;
      }, 0);
      const validationTarget = isPartialReturn && approvedTotal > 0
        ? declaredTotal - approvedTotal
        : declaredTotal;
      if (Math.abs(totalWithTax - validationTarget) > 0.01) {
        const label = isPartialReturn ? 'la porción devuelta' : 'el total del recibo';
        alert(
          `El total de los items + impuestos ($${totalWithTax.toFixed(2)}) no coincide con ${label} ($${validationTarget.toFixed(2)}).\n\nVerifica que los montos e impuestos estén correctos antes de enviar.`
        );
        return false;
      }
    }

    return true;
  }

  // ── Submission ──
  async function submitReceipt(status) {
    const contentItems = lineItems.filter(i => i.description?.trim() || i.amount);
    if (status === 'pending' && !validate()) return;
    if (status === 'pending' && !(await checkForDuplicate())) return;
    if (contentItems.length === 0 && status === 'draft' && !hasContent) {
      alert('No hay nada que guardar.');
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      if (receipt.file) form.append('file', receipt.file);
      form.append('vendor', receipt.vendor);
      form.append('date', receipt.date);
      form.append('paymentMethod', receipt.paymentMethod);
      if (receipt.cardType) form.append('cardType', receipt.cardType);
      if (receipt.invoiceNumber?.trim()) form.append('invoiceNumber', receipt.invoiceNumber.trim());
      form.append('status', status);
      if (receipt.receiptTotal) form.append('receiptTotal', receipt.receiptTotal);
      if (receipt.subtotal) form.append('subtotal', receipt.subtotal);
      form.append('aiSuggested', String(receipt.aiSuggested));
      form.append('aiConfidence', receipt.aiConfidence || '');
      form.append('items', JSON.stringify(contentItems.map(item => ({
        description: item.description,
        amount: item.amount,
        quantity: parseInt(item.quantity) || 1,
        unit: item.unit || null,
        taxRate: parseFloat(item.taxRate) || 0,
        category: item.category || null,
        costCenter: item.costCenter || null,
        supervisorId: item.supervisorId,
        projectId: item.projectId || null,
        aiSuggested: item.aiSuggested,
        aiFields: item.aiFields,
        isPersonal: item.isPersonal || false,
        expenseType: item.expenseType || 'project',
      }))));

      await client.post('/api/receipts', form);
      status === 'pending' ? onSubmitted(1) : onSavedDraft(1);
    } catch (err) {
      console.error('Submit error:', err);
      const serverMsg = err.response?.data?.error;
      alert(serverMsg || err.message || 'Error al enviar. Por favor, intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResubmit() {
    if (!validate()) return;
    if (!(await checkForDuplicate())) return;
    setSubmitting(true);
    try {
      const contentItems = lineItems.filter(i => i.description?.trim() || i.amount);
      const form = new FormData();
      if (receipt.newFile) form.append('file', receipt.newFile);
      form.append('vendor', receipt.vendor);
      form.append('date', receipt.date);
      form.append('paymentMethod', receipt.paymentMethod);
      if (receipt.cardType) form.append('cardType', receipt.cardType);
      if (receipt.invoiceNumber?.trim()) form.append('invoiceNumber', receipt.invoiceNumber.trim());
      if (receipt.receiptTotal) form.append('receiptTotal', receipt.receiptTotal);
      if (receipt.subtotal) form.append('subtotal', receipt.subtotal);
      form.append('items', JSON.stringify(contentItems.map(item => ({
        description: item.description,
        amount: item.amount,
        quantity: parseInt(item.quantity) || 1,
        unit: item.unit || null,
        taxRate: parseFloat(item.taxRate) || 0,
        category: item.category || null,
        costCenter: item.costCenter || null,
        supervisorId: item.supervisorId,
        projectId: item.projectId || null,
        aiSuggested: item.aiSuggested,
        aiFields: item.aiFields,
        isPersonal: item.isPersonal || false,
        expenseType: item.expenseType || 'project',
      }))));
      await client.patch(`/api/receipts/${receipt.receiptId}/resubmit`, form);
      onResubmitted();
    } catch (err) {
      console.error('Resubmit error:', err);
      const serverMsg = err.response?.data?.error;
      alert(serverMsg || 'Error al reenviar. Por favor, intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Draft edit: save or submit an existing draft ──
  function buildDraftPayload() {
    const contentItems = lineItems.filter(i => i.description?.trim() || i.amount);
    return {
      vendor: receipt.vendor,
      date: receipt.date,
      paymentMethod: receipt.paymentMethod,
      cardType: receipt.cardType || null,
      invoiceNumber: receipt.invoiceNumber?.trim() || '',
      items: JSON.stringify(contentItems.map(item => ({
        description: item.description,
        amount: item.amount,
        quantity: parseInt(item.quantity) || 1,
        unit: item.unit || null,
        taxRate: parseFloat(item.taxRate) || 0,
        category: item.category || null,
        costCenter: item.costCenter || null,
        supervisorId: item.supervisorId,
        projectId: item.projectId || null,
        aiSuggested: item.aiSuggested,
        aiFields: item.aiFields,
        isPersonal: item.isPersonal || false,
        expenseType: item.expenseType || 'project',
      }))),
    };
  }

  async function handleDraftSave() {
    setSubmitting(true);
    try {
      await client.patch(`/api/receipts/${receipt.receiptId}`, buildDraftPayload());
      onSavedDraft(1);
    } catch (err) {
      console.error('Draft save error:', err);
      alert(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDraftSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await client.patch(`/api/receipts/${receipt.receiptId}`, {
        ...buildDraftPayload(),
        status: 'pending',
      });
      onSubmitted(1);
    } catch (err) {
      console.error('Draft submit error:', err);
      alert(err.response?.data?.error || 'Error al enviar.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteDraft() {
    setSubmitting(true);
    try {
      await client.delete(`/api/receipts/${receipt.receiptId}`);
      if (onDiscarded) onDiscarded();
    } catch (err) {
      console.error('Delete draft error:', err);
      alert(err.response?.data?.error || 'Error al eliminar el borrador.');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  }

  const previewSrc = receipt.localPreview || receipt.previewUrl;

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      {showDraftModal && (
        <SaveDraftModal
          count={1}
          onSave={async () => { setShowDraftModal(false); isDraftEdit ? await handleDraftSave() : await submitReceipt('draft'); }}
          onDiscard={() => { setShowDraftModal(false); onDiscarded(); }}
          onKeep={() => setShowDraftModal(false)}
        />
      )}

      {/* Delete Draft Confirmation */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ ...bubbleStyle, width: 420, maxWidth: '90vw', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 10 }}>
              Eliminar Borrador
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 18 }}>
              ¿Estás seguro de que deseas eliminar este borrador? Esta acción no se puede deshacer.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                onClick={handleDeleteDraft}
                disabled={submitting}
                style={{
                  background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 18px', fontWeight: 600,
                  fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif', opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Eliminando…' : 'Eliminar Borrador'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  background: '#fff', color: '#475569', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 8, padding: '8px 18px', fontWeight: 600,
                  fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <button onClick={handleBack} style={{
          background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
          color: '#475569', borderRadius: 8, padding: '7px 14px',
          fontWeight: '600', fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> Regresar
        </button>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: '700', color: NAVY, margin: 0 }}>
            {isEditMode ? (isDraftEdit ? 'Editar Borrador' : (isPartialReturn ? 'Editar Items Devueltos' : 'Editar y Reenviar')) : 'Nuevo Reporte de Gastos'}
          </h2>
          {isEditMode && editReceipt.reviewReason && (
            <div style={{ fontSize: 13, color: '#d97706', marginTop: 3 }}>
              Devuelto: <strong>{editReceipt.reviewReason}</strong>
              {editReceipt.reviewNotes && ` — ${editReceipt.reviewNotes}`}
            </div>
          )}
        </div>
      </div>

      {/* ── Receipt-level section ── */}
      <div style={{ ...bubbleStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Recibo
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* File preview / upload */}
          <div>
            {hasFile ? (
              <div style={{ position: 'relative' }}>
                <div
                onClick={() => !receipt.analyzing && previewSrc && setShowReceiptModal(true)}
                title={receipt.analyzing ? '' : 'Ver recibo en grande'}
                style={{
                  width: 100, height: 100, borderRadius: 10, overflow: 'hidden',
                  border: `1px solid ${BORDER_COLOR}`, background: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: receipt.analyzing ? 'default' : 'pointer',
                }}>
                  {receipt.analyzing ? (
                    <Loader2 size={24} className="spin" style={{ color: AMBER }} />
                  ) : previewSrc && receipt.fileType?.startsWith('image/') ? (
                    <img src={previewSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FileText size={32} style={{ color: '#94a3b8' }} />
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {receipt.fileName}
                </div>
                <input ref={replaceFileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleReplaceFile} />
                <button onClick={() => replaceFileRef.current?.click()} style={{
                  fontSize: 11, color: AMBER, background: 'none', border: 'none',
                  cursor: 'pointer', fontWeight: 600, padding: 0, marginTop: 2,
                }}>
                  {isEditMode ? 'Reemplazar' : 'Cambiar archivo'}
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 100, height: 100, borderRadius: 10,
                  border: `2px dashed ${dragOver ? AMBER : BORDER_COLOR}`,
                  background: dragOver ? '#fffbeb' : '#fafafa',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', gap: 4, transition: 'all 0.15s',
                }}
              >
                <Upload size={20} style={{ color: dragOver ? AMBER : '#94a3b8' }} />
                <span style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', lineHeight: 1.2 }}>Subir recibo</span>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileInput} />
              </div>
            )}
          </div>

          {/* Receipt fields */}
          <div style={{ flex: 1, minWidth: 200, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Proveedor</label>
              <input style={fieldStyle} value={receipt.vendor}
                onChange={(e) => setReceipt(prev => ({ ...prev, vendor: e.target.value }))}
                placeholder="Nombre del proveedor" disabled={receipt.analyzing} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Fecha</label>
              <input style={fieldStyle} value={receipt.date} type="date"
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setReceipt(prev => ({ ...prev, date: e.target.value }))}
                disabled={receipt.analyzing} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Número de Factura</label>
              <input style={fieldStyle} value={receipt.invoiceNumber}
                onChange={(e) => setReceipt(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                placeholder="Ej: INV-0042" disabled={receipt.analyzing} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Método de Pago</label>
              <select style={selectStyle} value={receipt.paymentMethod}
                onChange={(e) => setReceipt(prev => ({ ...prev, paymentMethod: e.target.value, cardType: '' }))}>
                <option value="">— Seleccionar —</option>
                {Object.entries(PAYMENT_METHODS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            {receipt.paymentMethod === 'credit_card' && (
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Tarjeta</label>
                <select style={selectStyle} value={receipt.cardType}
                  onChange={(e) => setReceipt(prev => ({ ...prev, cardType: e.target.value }))}
                  disabled={receipt.analyzing}>
                  <option value="">— Seleccionar tarjeta —</option>
                  <option value="amex">American Express</option>
                  <option value="home_depot">Home Depot</option>
                  <option value="mastercard">Mastercard</option>
                  <option value="total">Total</option>
                  <option value="visa">Visa</option>
                </select>
              </div>
            )}
            {/* Subtotal — editable, used for non-reviewer validation */}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: !canEditTax ? NAVY : '#64748b', marginBottom: 4 }}>
                Subtotal del Recibo {!canEditTax && '✓'}
              </label>
              <input style={{
                ...fieldStyle,
                ...(receipt.subtotal && subtotal > 0 && !canEditTax && Math.abs(subtotal - parseFloat(receipt.subtotal)) > 0.02
                  ? { border: `2px solid #ef4444`, background: '#fef2f2' }
                  : {}),
              }}
                value={receipt.subtotal || ''}
                onChange={(e) => setReceipt(prev => ({ ...prev, subtotal: e.target.value }))}
                placeholder="0.00" type="number" min="0" step="0.01"
                disabled={receipt.analyzing || submitting} />
            </div>
            {/* Total — editable, used for reviewer validation */}
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: canEditTax ? NAVY : '#64748b', marginBottom: 4 }}>
                Total del Recibo {canEditTax && '✓'}
              </label>
              <input style={{
                ...fieldStyle,
                ...(receipt.receiptTotal && totalWithTax > 0 && canEditTax && Math.abs(totalWithTax - parseFloat(receipt.receiptTotal)) > 0.01
                  ? { border: `2px solid #ef4444`, background: '#fef2f2' }
                  : {}),
              }}
                value={receipt.receiptTotal || ''}
                onChange={(e) => setReceipt(prev => ({ ...prev, receiptTotal: e.target.value }))}
                placeholder="0.00" type="number" min="0" step="0.01"
                disabled={receipt.analyzing || submitting} />
            </div>
          </div>

          {/* AI confidence badge */}
          {receipt.aiConfidence && !receipt.analyzing && (
            <div style={{
              alignSelf: 'flex-start', fontSize: 11, color: '#64748b',
              background: '#f8fafc', border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap',
            }}>
              IA: {receipt.aiConfidence} confianza
            </div>
          )}
          {receipt.analyzing && (
            <div style={{ alignSelf: 'flex-start', fontSize: 12, color: AMBER, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={14} className="spin" /> Analizando recibo…
            </div>
          )}
        </div>
      </div>

      {/* ── Line Items section ── */}
      {lineItems.length > 0 && (
        <div style={{ ...bubbleStyle, padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          {/* Apply same supervisor toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            background: '#f8fafc', borderBottom: `1px solid ${BORDER_COLOR}`,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Items del Recibo
            </span>
            <div style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={applySameProject}
                onChange={(e) => handleApplySameProjectToggle(e.target.checked)}
                style={{ accentColor: AMBER }} />
              Mismo proyecto para todos
            </label>
            {applySameProject && (
              <select style={{ ...selectStyle, width: 180, fontSize: 12 }}
                value={globalProjectId}
                onChange={(e) => handleGlobalProjectChange(e.target.value)}>
                <option value="">— Proyecto —</option>
                {projects.filter(p => {
                  if (p.active === false) return false;
                  const isAdmin = p.project_type === 'administrative' || p.isAdminProject;
                  return applySameType
                    ? (globalExpenseType === 'admin' ? isAdmin : !isAdmin)
                    : true;
                }).map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={applySameType}
                onChange={(e) => handleApplySameTypeToggle(e.target.checked)}
                style={{ accentColor: AMBER }} />
              Mismo tipo para todos
            </label>
            {applySameType && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => handleGlobalExpenseTypeChange('project')} style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: globalExpenseType === 'project' ? `1.5px solid ${NAVY}` : `1px solid ${BORDER_COLOR}`,
                  background: globalExpenseType === 'project' ? '#e0e7ff' : '#fff',
                  color: globalExpenseType === 'project' ? NAVY : '#64748b',
                  fontFamily: 'Inter, sans-serif',
                }}>Proyecto</button>
                <button onClick={() => handleGlobalExpenseTypeChange('admin')} style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: globalExpenseType === 'admin' ? `1.5px solid ${AMBER}` : `1px solid ${BORDER_COLOR}`,
                  background: globalExpenseType === 'admin' ? '#fef3c7' : '#fff',
                  color: globalExpenseType === 'admin' ? '#92400e' : '#64748b',
                  fontFamily: 'Inter, sans-serif',
                }}>Administrativo</button>
              </div>
            )}
            {canEditTax && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                <input type="checkbox" checked={applySameTax}
                  onChange={(e) => handleApplySameTaxToggle(e.target.checked)}
                  style={{ accentColor: AMBER }} />
                Mismo Tax para todos
              </label>
            )}
            {canEditTax && applySameTax && (
              <select
                value={globalTaxRate}
                onChange={(e) => handleGlobalTaxRateChange(e.target.value)}
                style={{ ...fieldStyle, width: 'auto', fontSize: 12 }}
              >
                {PR_TAX_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}
          </div>

          {/* Desktop header */}
          <div className="staging-header" style={{
            display: 'grid',
            gridTemplateColumns: canEditTax
              ? '140px 1fr 50px 55px 70px 90px 55px 70px 50px 140px 36px'
              : '140px 1fr 50px 55px 70px 90px 50px 140px 36px',
            background: NAVY, color: '#fff',
            fontWeight: '600', fontSize: 11, letterSpacing: '0.04em',
            padding: '10px 14px', gap: 6,
          }}>
            <div>PROYECTO</div><div>DESCRIPCIÓN</div><div>CANT.</div><div>UNIDAD</div><div>P. UNIT.</div><div>MONTO</div>
            {canEditTax && <><div>IMP %</div><div>IMPUESTO</div></>}
            <div>PERS.</div><div>SUPERVISOR</div><div />
          </div>

          {/* Returned items indicator banner */}
          {isPartialReturn && (
            <div style={{
              padding: '8px 14px', background: '#fef2f2',
              borderBottom: `1px solid #fecaca`,
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
            }}>
              <AlertTriangle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span style={{ color: '#991b1b', fontWeight: 600 }}>
                Los siguientes items fueron devueltos y requieren corrección.
              </span>
            </div>
          )}

          {(() => {
            // Group items by project for display
            const itemsByProject = {};
            for (const item of lineItems) {
              const proj = projects.find(p => String(p.id) === String(item.projectId));
              const key = proj ? proj.name : (item.projectId || '(Sin Proyecto)');
              if (!itemsByProject[key]) itemsByProject[key] = { items: [], projectId: item.projectId };
              itemsByProject[key].items.push(item);
            }
            const projectGroups = Object.entries(itemsByProject);
            const showProjectHeaders = isPartialReturn && projectGroups.length >= 1;

            return projectGroups.map(([projectName, { items: projectItems }]) => (
              <div key={projectName}>
                {showProjectHeaders && (
                  <div style={{
                    padding: '7px 14px', background: '#f0f4ff',
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                  }}>
                    <span style={{ fontWeight: 600, color: NAVY }}>{projectName}</span>
                    {isPartialReturn && (
                      <span style={{
                        background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
                        borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 600, marginLeft: 8,
                      }}>
                        Devuelto
                      </span>
                    )}
                  </div>
                )}
                {projectItems.map((item) => {
                  const itemAmt = parseFloat(item.amount) || 0;
                  const itemTaxAmt = itemAmt * (parseFloat(item.taxRate) || 0) / 100;
                  return (
                  <div key={item.id}
                    className="staging-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: canEditTax
                        ? 'auto 130px 1fr 50px 55px 70px 90px 55px 70px 50px 130px 36px'
                        : 'auto 130px 1fr 50px 55px 70px 90px 50px 130px 36px',
                      padding: '10px 14px', gap: 6,
                      borderBottom: '1px solid #f1f5f9',
                      alignItems: 'center', background: '#fff',
                      ...(isPartialReturn ? { borderLeft: '3px solid #ef4444' } : {}),
                    }}
                  >
                    {/* Type toggle — always visible; interactive when per-item, read-only badge when global */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {applySameType ? (
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                          border: `1.5px solid ${item.expenseType === 'project' ? NAVY : AMBER}`,
                          background: item.expenseType === 'project' ? '#e0e7ff' : '#fef3c7',
                          color: item.expenseType === 'project' ? NAVY : '#92400e',
                          whiteSpace: 'nowrap', opacity: 0.75,
                        }}>
                          {item.expenseType === 'project' ? 'Proyecto' : 'Administ.'}
                        </span>
                      ) : (
                        <>
                          <button onClick={() => updateLineItem(item.id, 'expenseType', 'project')} style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                            border: item.expenseType === 'project' ? `1.5px solid ${NAVY}` : `1px solid ${BORDER_COLOR}`,
                            background: item.expenseType === 'project' ? '#e0e7ff' : '#fff',
                            color: item.expenseType === 'project' ? NAVY : '#94a3b8',
                            fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
                          }}>Proyecto</button>
                          <button onClick={() => updateLineItem(item.id, 'expenseType', 'admin')} style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                            border: item.expenseType === 'admin' ? `1.5px solid ${AMBER}` : `1px solid ${BORDER_COLOR}`,
                            background: item.expenseType === 'admin' ? '#fef3c7' : '#fff',
                            color: item.expenseType === 'admin' ? '#92400e' : '#94a3b8',
                            fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
                          }}>Administ.</button>
                        </>
                      )}
                    </div>

                    <select style={{ ...selectStyle, fontSize: 12, ...(validationErrors[item.id]?.projectId ? { border: '2px solid #ef4444', background: '#fef2f2' } : {}) }}
                      value={item.projectId || ''}
                      onChange={(e) => updateLineItem(item.id, 'projectId', e.target.value)}
                      disabled={applySameProject}>
                      <option value="">— Proyecto —</option>
                      {projects.filter(p => {
                        if (p.active === false) return false;
                        const isAdmin = p.project_type === 'administrative' || p.isAdminProject;
                        return item.expenseType === 'admin' ? isAdmin : !isAdmin;
                      }).map(p => (
                        <option key={p.id} value={String(p.id)}>{p.name}</option>
                      ))}
                    </select>

                    <input style={{ ...fieldStyle, fontSize: 12, ...(validationErrors[item.id]?.description ? { border: '2px solid #ef4444', background: '#fef2f2' } : {}) }}
                      value={item.description}
                      onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                      placeholder="Descripción" />

                    <input style={{ ...fieldStyle, fontSize: 12, width: '100%' }}
                      value={item.quantity}
                      onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                      placeholder="1" type="number" min="1" step="1" />

                    <input style={{ ...fieldStyle, fontSize: 11, width: '100%' }}
                      value={item.unit}
                      onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}
                      placeholder="unidad" />

                    <input style={{ ...fieldStyle, fontSize: 12, width: '100%' }}
                      value={item.unitPrice}
                      onChange={(e) => updateLineItem(item.id, 'unitPrice', e.target.value)}
                      placeholder="0.00" type="number" step="0.01" />

                    <input style={{ ...fieldStyle, fontSize: 12, ...(validationErrors[item.id]?.amount ? { border: '2px solid #ef4444', background: '#fef2f2' } : {}) }}
                      value={item.amount}
                      onChange={(e) => updateLineItem(item.id, 'amount', e.target.value)}
                      placeholder="0.00" type="number" step="0.01" />

                    {canEditTax && (
                      <>
                        <select style={{ ...fieldStyle, fontSize: 11, width: '100%' }}
                          value={PR_TAX_RATES.some(r => r.value === parseFloat(item.taxRate)) ? String(item.taxRate) : 'custom'}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              updateLineItem(item.id, 'taxRate', '');
                            } else {
                              updateLineItem(item.id, 'taxRate', e.target.value);
                            }
                          }}
                          disabled={applySameTax}
                        >
                          {PR_TAX_RATES.map(r => <option key={r.value} value={String(r.value)}>{r.label}</option>)}
                          <option value="custom">Otro…</option>
                        </select>
                        {!PR_TAX_RATES.some(r => r.value === parseFloat(item.taxRate)) && (
                          <input style={{ ...fieldStyle, fontSize: 11, width: 60, marginTop: 2 }}
                            value={item.taxRate || ''}
                            onChange={(e) => updateLineItem(item.id, 'taxRate', e.target.value)}
                            disabled={applySameTax}
                            placeholder="%" type="number" min="0" step="0.5" />
                        )}

                        <span style={{ fontSize: 12, color: '#64748b', textAlign: 'right', fontWeight: 500 }}>
                          {itemTaxAmt > 0 ? fmt(itemTaxAmt) : '—'}
                        </span>
                      </>
                    )}

                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={item.isPersonal || false}
                        onChange={(e) => updateLineItem(item.id, 'isPersonal', e.target.checked)}
                        style={{ accentColor: AMBER }} />
                      Personal
                    </label>

                    <select style={{
                        ...selectStyle, fontSize: 12,
                        opacity: 0.7,
                        cursor: 'not-allowed',
                        ...(validationErrors[item.id]?.supervisorId ? { border: '2px solid #ef4444', background: '#fef2f2' } : {}),
                      }}
                      value={item.supervisorId}
                      onChange={(e) => updateLineItem(item.id, 'supervisorId', e.target.value)}
                      disabled>
                      <option value="">— Supervisor —</option>
                      {supervisors.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                    </select>

                    <button onClick={() => removeLineItem(item.id)} style={{
                      background: 'none', border: 'none', color: '#94a3b8',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4, borderRadius: 4,
                    }} title="Eliminar item">
                      <X size={16} />
                    </button>
                  </div>
                  );
                })}
              </div>
            ));
          })()}

          {/* Add item + Footer total */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: '#f8fafc',
            borderTop: `1px solid ${BORDER_COLOR}`,
            flexWrap: 'wrap', gap: 8,
          }}>
            {(() => {
              // Employee compares subtotal; others compare total with tax
              const declaredVal = !canEditTax
                ? parseFloat(receipt.subtotal)
                : parseFloat(receipt.receiptTotal);
              const approvedTotal = !canEditTax
                ? approvedItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
                : approvedItems.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
              const targetTotal = isPartialReturn && approvedTotal > 0
                ? declaredVal - approvedTotal
                : declaredVal;
              const totalsMatch = !isNaN(targetTotal) && targetTotal > 0 && Math.abs(total - targetTotal) <= 0.02;
              return (
                <button onClick={addLineItem} disabled={totalsMatch} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  color: '#475569', cursor: totalsMatch ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 4,
                  opacity: totalsMatch ? 0.4 : 1,
                }}>
                  <Plus size={13} /> Agregar Item
                </button>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {lineItems.filter(i => i.description?.trim() || i.amount).length} items
              </span>
              {total > 0 && (
                <span style={{ fontWeight: '700', fontSize: 15, color: NAVY }}>{canEditTax ? 'Total' : 'Subtotal'}: {fmt(total)}</span>
              )}
              {(receipt.receiptTotal || receipt.subtotal) && total > 0 && (
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {canEditTax ? 'Total Recibo' : 'Subtotal Recibo'}: {fmt(canEditTax ? receipt.receiptTotal : receipt.subtotal)}
                </span>
              )}
            </div>
          </div>
          {/* Total mismatch warning */}
          {(() => {
            // Employee validates against subtotal; others against total with tax
            const declaredTarget = !canEditTax
              ? parseFloat(receipt.subtotal)
              : parseFloat(receipt.receiptTotal);
            const approvedAmt = !canEditTax
              ? approvedItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
              : approvedItems.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
            const target = isPartialReturn && approvedAmt > 0 ? declaredTarget - approvedAmt : declaredTarget;
            const displayTotal = total;
            const label = !canEditTax ? 'subtotal del recibo' : (isPartialReturn ? 'total de la porción devuelta' : 'total del recibo');
            return declaredTarget > 0 && displayTotal > 0 && Math.abs(displayTotal - target) > 0.02 ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', background: '#fef3c7',
                borderTop: `1px solid #fcd34d`,
                fontSize: 12, color: '#92400e',
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span>
                  <strong>El {canEditTax ? 'total' : 'subtotal'} de los items ({fmt(displayTotal)}) no coincide con el {label} ({fmt(target)}).</strong>
                  {' '}Verifica que todos los items estén incluidos.
                </span>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* ── Read-only items from other sections (for partially_returned receipts) ── */}
      {isPartialReturn && approvedItems.length > 0 && (() => {
        const otherByProject = {};
        for (const item of approvedItems) {
          const key = item.projectName || '(Sin Proyecto)';
          if (!otherByProject[key]) otherByProject[key] = [];
          otherByProject[key].push(item);
        }
        const otherProjectGroups = Object.entries(otherByProject);
        return (
          <div style={{ ...bubbleStyle, padding: 0, overflow: 'hidden', marginBottom: 16, opacity: 0.65 }}>
            <div style={{
              padding: '10px 16px', background: '#f1f5f9',
              borderBottom: `1px solid ${BORDER_COLOR}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Otros Items (no editables)
              </span>
            </div>
            {otherProjectGroups.map(([projectName, projectItems]) => {
              const supervisorName = projectItems[0]?.supervisorName || '—';
              const sectionTotal = projectItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
              return (
                <div key={projectName}>
                  <div style={{
                    padding: '7px 16px', background: '#f0f4ff',
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                  }}>
                    <span style={{ fontWeight: 600, color: NAVY }}>{projectName}</span>
                    <span style={{ color: '#94a3b8' }}>|</span>
                    <span style={{ color: '#64748b' }}>Supervisor: {supervisorName}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: NAVY }}>{fmt(sectionTotal)}</span>
                  </div>
                  {projectItems.map((item, idx) => (
                    <div key={item.id || idx} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 100px 90px',
                      padding: '10px 16px', gap: 8,
                      borderBottom: '1px solid #f1f5f9',
                      alignItems: 'center', background: '#fafafa',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>{item.description || '—'}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textAlign: 'right' }}>{fmt(item.amount)}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, textAlign: 'center',
                        padding: '2px 8px', borderRadius: 20,
                        background: item.status === 'approved' ? '#e8f5e9' : item.status === 'pending' ? '#FFF8E1' : '#f1f5f9',
                        color: item.status === 'approved' ? '#1b5e20' : item.status === 'pending' ? '#7A5800' : '#64748b',
                        border: `1px solid ${item.status === 'approved' ? '#81C784' : item.status === 'pending' ? '#FFD54F' : BORDER_COLOR}`,
                      }}>
                        {item.status === 'approved' ? 'Aprobado' : item.status === 'pending' ? 'Pendiente' : item.status}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── Upload zone hint when no file yet ── */}
      {!hasFile && !isEditMode && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? AMBER : BORDER_COLOR}`,
            borderRadius: 12, padding: '24px 16px',
            cursor: 'pointer', textAlign: 'center',
            background: dragOver ? '#fffbeb' : '#fafafa',
            transition: 'all 0.15s', marginBottom: 16,
          }}
        >
          <ImagePlus size={24} style={{ color: dragOver ? AMBER : '#94a3b8', marginBottom: 6 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
            Sube una foto del recibo para auto-completar con IA
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            También puedes llenar los items manualmente arriba
          </div>
        </div>
      )}

      {/* ── Action buttons ── */}
      {hasContent && (
        <div className="action-btns" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          {isEditMode && !isDraftEdit ? (
            /* sent_back edit mode — backend deleteReceipt only allows status='draft',
               so no delete affordance here; the chofer must Reenviar or send back to admin */
            <button onClick={handleResubmit}
              disabled={submitting || receipt.analyzing}
              style={{
                ...navyBtn, display: 'flex', alignItems: 'center', gap: 8,
                opacity: submitting || receipt.analyzing ? 0.6 : 1,
                cursor: submitting || receipt.analyzing ? 'not-allowed' : 'pointer',
              }}>
              {submitting
                ? <><Loader2 size={15} className="spin" /> Enviando…</>
                : <><Upload size={15} /> Reenviar para Revisión</>
              }
            </button>
          ) : (
            /* new receipt OR draft edit mode — Eliminar (drafts only) + Guardar + Enviar */
            <>
              {isDraftEdit && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={submitting}
                  style={deleteBtnStyle(submitting)}
                >
                  <Trash2 size={14} /> Eliminar Borrador
                </button>
              )}
              <button onClick={isDraftEdit ? handleDraftSave : () => submitReceipt('draft')}
                disabled={submitting || receipt.analyzing}
                style={{
                  background: '#fff', color: '#475569', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 8, padding: '10px 18px', fontWeight: '600',
                  fontSize: 14, cursor: submitting || receipt.analyzing ? 'not-allowed' : 'pointer',
                  opacity: submitting || receipt.analyzing ? 0.6 : 1,
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                <Save size={15} /> Guardar Borrador
              </button>
              <button onClick={isDraftEdit ? handleDraftSubmit : () => submitReceipt('pending')}
                disabled={submitting || receipt.analyzing}
                style={{
                  ...navyBtn, display: 'flex', alignItems: 'center', gap: 8,
                  opacity: submitting || receipt.analyzing ? 0.6 : 1,
                  cursor: submitting || receipt.analyzing ? 'not-allowed' : 'pointer',
                }}>
                {submitting
                  ? <><Loader2 size={15} className="spin" /> Enviando…</>
                  : <>Enviar Reporte <ArrowRight size={15} /></>
                }
              </button>
            </>
          )}
        </div>
      )}

      {/* Receipt full-size viewer modal */}
      {showReceiptModal && previewSrc && (
        <ReceiptViewerModal
          receipt={{
            vendor: receipt.vendor || 'Recibo',
            status: 'draft',
            amount: total || null,
            date: receipt.date || null,
            fileName: receipt.fileName,
            fileType: receipt.fileType,
            previewUrl: previewSrc,
          }}
          hasPrev={false}
          hasNext={false}
          onPrev={() => {}}
          onNext={() => {}}
          onClose={() => setShowReceiptModal(false)}
        />
      )}
    </div>
  );
}

// Shared delete-button style for the action bar (draft + sent_back paths).
const deleteBtnStyle = (submitting) => ({
  background: 'none', color: '#dc2626', border: '1px solid #fecaca',
  borderRadius: 8, padding: '10px 14px', fontWeight: 600,
  fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
  fontFamily: 'Inter, sans-serif',
  display: 'flex', alignItems: 'center', gap: 6,
  opacity: submitting ? 0.6 : 1,
});
